import * as THREE from "three";
import {ShaderUniform} from "./CBARMaterial";

const rgb2hsv = `
    vec3 rgb2hsv(in vec3 c) {
        vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
        vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
        vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));
        
        float d = q.x - min(q.w, q.y);
        float e = 1.0e-10;
        return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
    }
`;

const hsv2rgb = `
    vec3 hsv2rgb(in vec3 c) {
        vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
        vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
        return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
    }
`;

export const ColorFunctions = [rgb2hsv, hsv2rgb];

export const CropPosition = `
    vec2 cropPosition(in vec2 pos, in vec4 crop) {   
        return vec2(crop.x + pos.x * crop.z, crop.y + pos.y * crop.w);
    }
`;

const MaskValue = `
    const float pi2 = 6.28318530718; // Pi*2
    const float directions = 16.0; // BLUR DIRECTIONS (Default 16.0 - More is better but slower)
    const float quality = 3.0; // BLUR QUALITY (Default 4.0 - More is better but slower)
        
    float maskValue(in sampler2D tex, in vec2 pos, in vec2 blendRadius) {
        float pixelValue = texture2D(tex, pos).g;
        if (pixelValue > 0.99) {
            return pixelValue;
        }
 
        // Blur calculations
        for (float d=0.0; d < pi2; d+=pi2/directions) {
            for(float i=1.0/quality; i <= 1.0; i+=1.0/quality) {
                pixelValue += texture2D(tex, pos + vec2(cos(d), sin(d)) * blendRadius * i).g;
            }
        }
        
        // Output to screen
        pixelValue /= quality * directions - 15.0;
        return pixelValue;
    }
`

export const MaskFunctions = [MaskValue, CropPosition];

export type UniformedDictionary = { [uniform: string]: ShaderUniform }

export const BaseUniforms = (resolution:{x:number, y:number})=>{
    let uniforms:UniformedDictionary = {};
    uniforms["u_viewportSize"] = {type:"vec2", value:resolution}
    return uniforms;
}

export const MaskUniforms = (resolution:{x:number, y:number})=>{
    let uniforms:UniformedDictionary = {};

    uniforms["u_maskIndex"] = {type:"int", value:-1}
    uniforms["u_maskBlendDistance"] = {type:"float", value:8.0}
    uniforms["u_maskCrop"] = {type:"vec4", value:new THREE.Vector4(0,0, resolution.x, resolution.y)}

    return uniforms;
}

export const LightingUniforms = ()=>{
    let uniforms:UniformedDictionary = {};

    //lighting
    uniforms["u_lightingFactor"] = {type:"float", value:1.0}
    uniforms["u_lightingOffset"] = {type:"float", value:0.0}
    uniforms["u_smoothStep"] = {type:"float", value:1.0}

    return uniforms;
}

export const StandardUniforms = (resolution:{x:number, y:number})=>{
    return {...BaseUniforms(resolution), ...MaskUniforms(resolution), ...LightingUniforms()};
}

export const SerializeUniforms = (uniforms:UniformedDictionary) =>{
    let prepend = ""
    Object.entries(uniforms).forEach(
        ([key, info]) => {
            prepend += `uniform ${info.type} ${key};\n`
        }
    );
    return prepend;
}

export const setShaderUniforms = (shader:THREE.Shader, uniforms:UniformedDictionary) =>{
    Object.entries(uniforms).forEach(
        ([key, info]) => {
            shader.uniforms[key] = {value: info.value};
        }
    );
}